import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import TopBar from './TopBar';
import { getOrderItemById, getOrderById, updateOrderItemStatus } from '../services/orderService';
import { getPickupPointById } from '../services/pickupPointService';
import { getFoodItemById, getFoodImageUrl } from '../services/foodItemService';
import { getProfileByUserId, updateProfile } from '../services/profileService';
import { shouldAutoComplete } from '../utils/orderTimeUtils';
import './VendorOrderDetailPage.css';

const VendorOrderDetailPage = () => {
  const navigate = useNavigate();
  const { orderItemId } = useParams();
  const { user } = useAuth();
  const { showSuccess, showError, showInfo } = useToast();
  const location = useLocation();
  const [orderItem, setOrderItem] = useState(null);
  const [order, setOrder] = useState(null);
  const [foodItem, setFoodItem] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [pickupLocation, setPickupLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [vendorProfile, setVendorProfile] = useState(null);
  const [notificationSaving, setNotificationSaving] = useState(false);

  const handleToggleSalesNotification = async (e) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    if (!vendorProfile) return;
    const newVal = !vendorProfile.sales_notification;
    try {
      setNotificationSaving(true);
      await updateProfile(vendorProfile.$id, { sales_notification: newVal });
      setVendorProfile(prev => ({ ...(prev || {}), sales_notification: newVal }));
      showSuccess(`Notifications ${newVal ? 'enabled' : 'disabled'} successfully!`);
    } catch (err) {
      console.error('Failed to update vendor notification setting:', err);
      showError('Failed to update notification preference. Please try again.');
    } finally {
      setNotificationSaving(false);
    }
  };

  useEffect(() => {
    const fetchOrderItemDetails = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch the order item
        const orderItemData = await getOrderItemById(orderItemId);

        // Fetch the food item to verify vendor ownership
        const foodItemData = await getFoodItemById(orderItemData.food_id);

        // Verify this food item belongs to the current vendor
        if (foodItemData.vendor_id !== user.$id) {
          setError('You do not have permission to manage this order.');
          setLoading(false);
          return;
        }

        // Fetch the parent order
        const orderData = await getOrderById(orderItemData.order_id);

        // Fetch customer profile
        let customerData = null;
        try {
          customerData = await getProfileByUserId(orderData.customer_id);
        } catch (customerError) {
          console.error('Error fetching customer profile:', customerError);
        }

        // Fetch pickup location from the food item (use the food item's pickup_id)
        let pickupData = null;
        if (foodItemData.pickup_id) {
          try {
            pickupData = await getPickupPointById(foodItemData.pickup_id);
          } catch (pickupError) {
            console.error('Error fetching pickup point:', pickupError);
          }
        }

        setOrderItem({
          id: orderItemData.$id,
          foodId: orderItemData.food_id,
          orderId: orderItemData.order_id,
          quantity: orderItemData.quantity,
          priceAtPurchase: orderItemData.price_at_purchase,
          status: orderItemData.status || 'pending'
        });

        setOrder({
          id: orderData.$id,
          customerId: orderData.customer_id,
          totalAmount: orderData.total_amount,
          orderDate: orderData.order_date
        });

        setFoodItem({
          id: foodItemData.$id,
          name: foodItemData.item_name,
          price: foodItemData.price,
          pickupTime: foodItemData.pickup_time,
          imageId: foodItemData.image_id,
          imageUrl: foodItemData.image_id ? getFoodImageUrl(foodItemData.image_id) : null
        });

        setCustomer(customerData ? {
          id: customerData.$id,
          name: customerData.full_name
        } : { name: 'Unknown Customer' });

        setPickupLocation(pickupData ? (pickupData.fullName || pickupData.name) : 'Not specified');

        // Fetch vendor's profile so we can read/update notification preference
        try {
          const vendorProf = await getProfileByUserId(user.$id);
          setVendorProfile(vendorProf);
        } catch (profErr) {
          console.error('Error fetching vendor profile:', profErr);
        }

        setSelectedStatus(orderItemData.status || 'pending');
      } catch (err) {
        console.error('Error fetching order item details:', err);
        setError('Failed to load order details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderItemDetails();
  }, [user, orderItemId, navigate]);

  // Auto-complete order item if auto-complete period has passed
  // This ensures auto-complete works when vendor views the order too
  useEffect(() => {
    const autoCompleteItem = async () => {
      if (orderItem && foodItem &&
        orderItem.status !== 'completed' &&
        orderItem.status !== 'not_completed' &&
        shouldAutoComplete(foodItem.pickupTime)) {
        try {
          console.log(`Auto-completing order item ${orderItem.id} (vendor view) - buyer did not respond within auto-complete period`);
          await updateOrderItemStatus(orderItem.id, 'completed');
          setOrderItem(prev => ({ ...prev, status: 'completed' }));
        } catch (err) {
          console.error('Error auto-completing order item:', err);
        }
      }
    };

    if (orderItem && foodItem) {
      autoCompleteItem();
      // Re-check periodically (every minute)
      const interval = setInterval(autoCompleteItem, 60000);
      return () => clearInterval(interval);
    }
  }, [orderItem?.id, foodItem?.pickupTime]);

  const handleUpdateStatus = async () => {
    if (!selectedStatus || selectedStatus === orderItem.status) {
      return;
    }

    // Vendors cannot set status to 'completed' or 'not_completed'
    if (selectedStatus === 'completed' || selectedStatus === 'not_completed') {
      showInfo('Only buyers can mark orders as completed or not completed.');
      return;
    }

    try {
      setUpdating(true);
      await updateOrderItemStatus(orderItem.id, selectedStatus);
      setOrderItem(prev => ({ ...prev, status: selectedStatus }));
      showSuccess('Order status updated successfully!');
    } catch (err) {
      console.error('Error updating order status:', err);
      showError('Failed to update order status. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }) + ', ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatOrderId = (id) => {
    if (!id) return '';
    // Display the raw Appwrite document ID ($id) from the order document
    return id;
  };

  const formatPickupTime = (timeString) => {
    if (!timeString || timeString === 'Not specified') return 'Not specified';
    try {
      const date = new Date(timeString);
      if (isNaN(date.getTime())) return timeString;
      return date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }) + ', ' + date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timeString;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'preparing': return 'Preparing';
      case 'ready_for_pickup': return 'Ready for Pickup';
      case 'completed': return 'Completed';
      case 'not_completed': return 'Not Completed';
      default: return 'Unknown';
    }
  };

  const getProgressStep = (status) => {
    switch (status) {
      case 'pending': return 0;
      case 'preparing': return 1;
      case 'ready_for_pickup': return 2;
      case 'completed': return 3;
      case 'not_completed': return -1;
      default: return 0;
    }
  };

  // Helper to get the index of the furthest completed step for rendering purposes
  const renderProgressIndex = (status) => {
    if (status === 'not_completed') return 2;
    return getProgressStep(status);
  };

  if (loading) {
    return (
      <div className="vendor-order-detail-page">
        <TopBar />
        <div className="vendor-order-detail-container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading order details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="vendor-order-detail-page">
        <TopBar />
        <div className="vendor-order-detail-container">
          <div className="error-state">
            <p>{error}</p>
            <button onClick={() => navigate('/orders', { state: { viewMode: location.state?.viewMode || 'vendor', tab: location.state?.tab || 'all' } })} className="back-btn">
              Back to Orders
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalForThisItem = orderItem ? orderItem.priceAtPurchase * orderItem.quantity : 0;

  return (
    <div className="vendor-order-detail-page">
      <TopBar />
      <div className="vendor-order-detail-container">
        <button className="back-link" onClick={() => navigate('/orders', { state: { viewMode: location.state?.viewMode || 'vendor', tab: location.state?.tab || 'all' } })}>
          ← Back to Orders
        </button>

        <h1 className="page-title">Update Order Status</h1>
        <p className="page-subtitle">Manage and update the status of customer orders</p>

        {/* Sales notification toggle for vendor */}
        <div className="vendor-notification-toggle">
          <label className="toggle-label">
            Sales Notifications
            <div className="switch" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={!!vendorProfile?.sales_notification}
                onChange={handleToggleSalesNotification}
                disabled={notificationSaving}
                aria-label="Toggle sales notifications"
              />
              <span className="slider"></span>
            </div>
          </label>
          <div className="toggle-note">Receive email notifications when new orders arrive</div>
        </div>

        {orderItem && foodItem && (
          <>
            {/* Order Summary Card */}
            <div className="order-summary-card">
              <h2 className="section-title">Order Summary</h2>
              <div className="summary-content">
                <div className="food-image">
                  {foodItem.imageUrl ? (
                    <img src={foodItem.imageUrl} alt={foodItem.name} />
                  ) : (
                    <div className="image-placeholder">Food Image</div>
                  )}
                </div>
                <div className="food-details">
                  <h3 className="food-name">{foodItem.name}</h3>
                  <p className="food-quantity">Quantity: {orderItem.quantity}</p>
                  <p className="order-id">Order Item ID: {formatOrderId(orderItem.id)}</p>
                  <div className="customer-info">
                    <p><span className="label">Customer:</span> {customer?.name}</p>
                  </div>
                  <div className="pickup-info">
                    <p><span className="label">Pickup Location:</span> {pickupLocation}</p>
                    <p><span className="label">Pickup Time:</span> {formatPickupTime(foodItem.pickupTime)}</p>
                  </div>
                </div>
                <div className="price-section">
                  <span className="total-price">RM {totalForThisItem.toFixed(2)}</span>
                  <span className="price-label">Total Amount</span>
                </div>
              </div>
            </div>

            {/* Order Progress */}
            <div className="order-progress-card">
              <h2 className="section-title">Order Progress</h2>
              <div className="progress-tracker">
                <div className={`progress-step ${renderProgressIndex(orderItem.status) >= 0 ? 'completed' : ''} ${renderProgressIndex(orderItem.status) === 0 ? 'current' : ''}`}>
                  <div className="step-icon">
                    {renderProgressIndex(orderItem.status) > 0 ? '✓' : ''}
                  </div>
                  <span className="step-label">Pending</span>
                </div>
                <div className={`progress-line ${renderProgressIndex(orderItem.status) >= 1 ? 'active' : ''}`}></div>
                <div className={`progress-step ${renderProgressIndex(orderItem.status) >= 1 ? 'completed' : ''} ${renderProgressIndex(orderItem.status) === 1 ? 'current' : ''}`}>
                  <div className="step-icon">
                    {renderProgressIndex(orderItem.status) > 1 ? '✓' : '🍳'}
                  </div>
                  <span className="step-label">Preparing</span>
                </div>
                <div className={`progress-line ${renderProgressIndex(orderItem.status) >= 2 ? 'active' : ''}`}></div>
                <div className={`progress-step ${renderProgressIndex(orderItem.status) >= 2 ? 'completed' : ''} ${renderProgressIndex(orderItem.status) === 2 ? 'current' : ''}`}>
                  <div className="step-icon">
                    {renderProgressIndex(orderItem.status) > 2 ? '✓' : '📦'}
                  </div>
                  <span className="step-label">Ready for Pickup</span>
                </div>
                <div className={`progress-line ${(orderItem.status === 'not_completed' || renderProgressIndex(orderItem.status) >= 3) ? 'active' : ''}`}></div>

                {orderItem.status === 'not_completed' ? (
                  <div className={`progress-step not-completed current`}>
                    <div className="step-icon">✗</div>
                    <span className="step-label">Not Completed</span>
                  </div>
                ) : (
                  <div className={`progress-step ${renderProgressIndex(orderItem.status) >= 3 ? 'completed' : ''} ${renderProgressIndex(orderItem.status) === 3 ? 'current' : ''}`}>
                    <div className="step-icon">{renderProgressIndex(orderItem.status) >= 3 ? '✓' : '✅'}</div>
                    <span className="step-label">Completed</span>
                  </div>
                )}
              </div>
            </div>

            {/* Update Status Section */}
            <div className="update-status-card">
              <h2 className="section-title">Update Status</h2>

              {/* Vendors can only update to pending, preparing, or ready_for_pickup */}
              {orderItem.status !== 'completed' && orderItem.status !== 'not_completed' ? (
                <>
                  <div className="status-selection">
                    <p className="status-instruction">Select New Status (Buyers will confirm completion)</p>
                    <div className="status-buttons">
                      <button
                        className={`status-btn ${selectedStatus === 'pending' ? 'active' : ''}`}
                        onClick={() => setSelectedStatus('pending')}
                      >
                        Pending
                      </button>
                      <button
                        className={`status-btn ${selectedStatus === 'preparing' ? 'active' : ''}`}
                        onClick={() => setSelectedStatus('preparing')}
                      >
                        Preparing
                      </button>
                      <button
                        className={`status-btn ${selectedStatus === 'ready_for_pickup' ? 'active' : ''}`}
                        onClick={() => setSelectedStatus('ready_for_pickup')}
                      >
                        Ready for Pickup
                      </button>
                    </div>
                  </div>
                  <button
                    className="update-btn"
                    onClick={handleUpdateStatus}
                    disabled={updating || selectedStatus === orderItem.status}
                  >
                    {updating ? (
                      <>
                        <span className="btn-spinner"></span>
                        Updating...
                      </>
                    ) : (
                      <>
                        <span className="refresh-icon">⟳</span>
                        Update Order Status
                      </>
                    )}
                  </button>
                </>
              ) : (
                <div className="status-complete-message">
                  <p>This order has been {orderItem.status === 'completed' ? 'completed by the buyer' : 'marked as not completed by the buyer'}.</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VendorOrderDetailPage;
